/**
 * Shares a single `useUpdateCrmAssociation` mutation instance between the
 * inline Assignee editor (`TaskFieldRows`, inside `TaskRecordOverview`)
 * and the footer's Reassign picker (`TaskActionFooter`) — both write the
 * same `ahoy_task_to_owner` association on the same object.
 *
 * `TaskRecordOverview` and `TaskActionFooter` are SIBLINGS under
 * `RecordViewProvider`, not parent/child, so sharing one mutation between them
 * needs either a context or drilling the mutation through `RecordViewProvider`
 * and back down two separate subtrees. The context is the narrower change:
 * `RecordViewProvider` instantiates the mutation once and provides it here; each
 * consumer reads it via `useTaskAssigneeMutation()` instead of calling
 * `useUpdateCrmAssociation` itself. One shared instance means one
 * `isPending` — a write from either surface disables the other's affordance
 * until it settles, instead of racing two independent in-flight writes
 * against the same association.
 */

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import type { UseMutationResult } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext } from 'react';

import type { UpdateCrmAssociationVariables } from '@/lib/crm/hooks/useUpdateCrmAssociation';

export type TaskAssigneeMutation = UseMutationResult<
  unknown,
  Error,
  UpdateCrmAssociationVariables
>;

const TaskAssigneeMutationContext = createContext<TaskAssigneeMutation | null>(
  null
);

export function TaskAssigneeMutationProvider({
  mutation,
  children,
}: {
  mutation: TaskAssigneeMutation;
  children: ReactNode;
}) {
  return (
    <TaskAssigneeMutationContext.Provider value={mutation}>
      {children}
    </TaskAssigneeMutationContext.Provider>
  );
}

export function useTaskAssigneeMutation(): TaskAssigneeMutation {
  const mutation = useContext(TaskAssigneeMutationContext);
  if (!mutation) {
    throw new Error(
      'useTaskAssigneeMutation must be used within a TaskAssigneeMutationProvider'
    );
  }
  return mutation;
}
